import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Package, Plus, Search, Loader2, X, Clock, QrCode, ScanLine } from 'lucide-react'
import QRCode from 'qrcode'
import { apiGet, cachedGet } from '../lib/api.js'
import { usePersistedState } from '../lib/persist.js'
import {
  ASSET_STATUS, statusInfo, assetAge, indexTypes, typeLabel, relabelBranch, fmtMoney, checkDueState,
  profileImage, imageThumbSrc, assetDeepLink, assetLabelLine, isDepreciable,
} from '../lib/asset.js'
import AssetEditor from '../components/asset/AssetEditor.jsx'
import QrScanner from '../components/asset/QrScanner.jsx'
import { checkPath } from '../lib/assetScan.js'
import { openLabelPrint } from '../lib/assetLabel.js'
import Pager from '../components/Pager.jsx'

const PER_PAGE = 30

function StatusPill({ status }) {
  const s = statusInfo(status)
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11.5px] font-medium ring-1 ${s.tone}`}>{s.label}</span>
}

export default function Assets() {
  const navigate = useNavigate()
  const location = useLocation()
  const [types, setTypes] = useState([])
  const [branches, setBranches] = useState([])

  const [search, setSearch] = usePersistedState('asset:search', '')
  const [typeId, setTypeId] = usePersistedState('asset:type', '')
  const [branchId, setBranchId] = usePersistedState('asset:branch', '')
  const [status, setStatus] = usePersistedState('asset:status', '')
  const [dueOnly, setDueOnly] = usePersistedState('asset:dueOnly', false)
  const [page, setPage] = useState(1)
  // When filtering by "due for check" (computed client-side from check[]), pull a
  // big page so the filter sees every asset in one shot rather than one server page.
  const perPage = dueOnly ? 500 : PER_PAGE

  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [pageTotal, setPageTotal] = useState(1)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')

  const [editor, setEditor] = useState(null) // { mode, assetId }
  const [scanning, setScanning] = useState(false)
  const reqId = useRef(0)

  // reference data (once per session)
  useEffect(() => {
    cachedGet('/api/asset-types').then((r) => setTypes(Array.isArray(r) ? r : (r?.items || []))).catch(() => {})
    cachedGet('/api/branches').then((r) => setBranches(Array.isArray(r) ? r : (r?.items || []))).catch(() => {})
  }, [])

  // "สร้างสินทรัพย์จากใบนี้" from the Expense editor lands here with ?new=1&… —
  // open the create drawer pre-filled from the expense, then strip the query so a
  // refresh (or closing the drawer) doesn't reopen it.
  useEffect(() => {
    const sp = new URLSearchParams(location.search)
    if (sp.get('new') !== '1') return
    setEditor({
      mode: 'create',
      prefill: {
        name: sp.get('name') || '',
        price: sp.get('price') || '',
        date: (sp.get('date') || '').slice(0, 10),
        expense_id: Number(sp.get('expense_id')) || 0,
      },
    })
    navigate('/manager/assets', { replace: true })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const { byId } = useMemo(() => indexTypes(types), [types])
  const branchName = useMemo(() => {
    const m = new Map()
    for (const b of branches) m.set(Number(b.id), relabelBranch(b.name))
    return m
  }, [branches])

  // debounce the search box before it drives the query
  const [debounced, setDebounced] = useState(search)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300)
    return () => clearTimeout(t)
  }, [search])

  // reset to page 1 whenever a filter changes
  useEffect(() => { setPage(1) }, [debounced, typeId, branchId, status, dueOnly])

  useEffect(() => {
    const id = ++reqId.current
    setLoading(true)
    setErr('')
    apiGet('/api/assets', {
      page, per_page: perPage,
      search: debounced.trim() || undefined,
      asset_type_id: typeId || undefined,
      branch_id: branchId || undefined,
      status: status || undefined,
    })
      .then((r) => {
        if (id !== reqId.current) return
        const items = Array.isArray(r) ? r : (r?.items || [])
        setRows(items)
        setTotal(r?.itemsTotal ?? items.length)
        setPageTotal(r?.pageTotal ?? 1)
      })
      .catch((e) => { if (id === reqId.current) setErr(String(e.message || e)) })
      .finally(() => { if (id === reqId.current) setLoading(false) })
  }, [page, debounced, typeId, branchId, status, dueOnly])

  function refresh() {
    // bump reqId path by toggling page to itself via a no-op state churn
    setEditor(null)
    const id = ++reqId.current
    setLoading(true)
    apiGet('/api/assets', {
      page, per_page: perPage,
      search: debounced.trim() || undefined,
      asset_type_id: typeId || undefined,
      branch_id: branchId || undefined,
      status: status || undefined,
    })
      .then((r) => {
        if (id !== reqId.current) return
        const items = Array.isArray(r) ? r : (r?.items || [])
        setRows(items); setTotal(r?.itemsTotal ?? items.length); setPageTotal(r?.pageTotal ?? 1)
      })
      .catch(() => {})
      .finally(() => { if (id === reqId.current) setLoading(false) })
  }

  const displayRows = dueOnly ? rows.filter((a) => checkDueState(a).due) : rows

  async function printAllQR() {
    if (!displayRows.length) return
    const labels = await Promise.all(displayRows.map(async (a) => ({
      url: await QRCode.toDataURL(assetDeepLink(a), { margin: 1, width: 220 }).catch(() => ''),
      line: assetLabelLine(a, byId),
      serial: a.serial_num,
      id: a.id,
    })))
    openLabelPrint(labels)
  }

  const hasFilter = search || typeId || branchId || status || dueOnly
  const SELECT = 'rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-700 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'

  return (
    <div className="px-10 py-8 max-w-[1200px]">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-[22px] font-semibold text-slate-900 tracking-tight mb-1 flex items-center gap-2">
            <Package size={22} className="text-brand-600" strokeWidth={1.75} /> สินทรัพย์ (Assets)
          </h1>
          <p className="text-[14px] text-slate-500">ทะเบียนทรัพย์สินของคลินิก — เก้าอี้ทำฟัน อุปกรณ์การแพทย์ คอมพิวเตอร์ และเครื่องใช้</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* Scan → straight to the check screen. This is the start of a check
              round: scan, photograph, save, scan the next one. */}
          <button
            onClick={() => setScanning(true)}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-[14px] font-semibold text-brand-700 bg-brand-50 hover:bg-brand-100"
            title="สแกน QR บนสติกเกอร์เพื่อบันทึกการตรวจเช็ก"
          >
            <ScanLine size={16} /> สแกนตรวจเช็ก
          </button>
          <button
            onClick={() => setEditor({ mode: 'create' })}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-[14px] font-semibold text-white bg-brand-600 hover:bg-brand-700 shadow-sm"
          >
            <Plus size={16} /> เพิ่มสินทรัพย์
          </button>
        </div>
      </div>

      {/* filters */}
      <div className="flex flex-wrap items-center gap-2.5 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="ค้นหาชื่อ / ยี่ห้อ / รุ่น / Serial…"
            className="w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 py-2 text-[14px] text-slate-800 placeholder:text-slate-400 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10"
          />
        </div>
        <select value={typeId} onChange={(e) => setTypeId(e.target.value)} className={SELECT}>
          <option value="">ทุกประเภท</option>
          {[...indexTypes(types).groups.entries()].map(([g, rs]) => (
            <optgroup key={g} label={g}>
              {rs.map((r) => <option key={r.id} value={r.id}>{r.subtype || r.Type}</option>)}
            </optgroup>
          ))}
        </select>
        <select value={branchId} onChange={(e) => setBranchId(e.target.value)} className={SELECT}>
          <option value="">ทุกสาขา</option>
          {branches.map((b) => <option key={b.id} value={b.id}>{relabelBranch(b.name)}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={SELECT}>
          <option value="">ทุกสถานะ</option>
          {ASSET_STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <button
          onClick={() => setDueOnly((v) => !v)}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-[13px] font-medium ring-1 transition ${dueOnly ? 'bg-amber-50 text-amber-700 ring-amber-200' : 'bg-white text-slate-500 ring-slate-200 hover:bg-slate-50'}`}
        >
          <Clock size={14} /> ถึงกำหนดตรวจ
        </button>
        {hasFilter && (
          <button
            onClick={() => { setSearch(''); setTypeId(''); setBranchId(''); setStatus(''); setDueOnly(false) }}
            className="flex items-center gap-1 px-2.5 py-2 rounded-lg text-[13px] text-slate-500 hover:bg-slate-100"
          >
            <X size={14} /> ล้าง
          </button>
        )}
        <button
          onClick={printAllQR}
          disabled={!displayRows.length}
          className="ml-auto flex items-center gap-1.5 px-3 py-2 rounded-xl text-[13px] font-medium text-slate-600 ring-1 ring-slate-200 bg-white hover:bg-slate-50 disabled:opacity-50"
          title="พิมพ์ QR ของสินทรัพย์ที่แสดงอยู่ทั้งหมด"
        >
          <QrCode size={14} /> พิมพ์ QR ({displayRows.length})
        </button>
      </div>

      {/* table */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13.5px]">
            <thead>
              <tr className="text-left text-[12px] font-medium text-slate-400 border-b border-slate-100">
                <th className="px-4 py-2.5">สินทรัพย์</th>
                <th className="px-4 py-2.5">ประเภท</th>
                <th className="px-4 py-2.5">Serial</th>
                <th className="px-4 py-2.5">สาขา</th>
                <th className="px-4 py-2.5">อายุ</th>
                <th className="px-4 py-2.5">สถานะ</th>
                <th className="px-4 py-2.5 text-right">ราคาซื้อ</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400"><Loader2 size={18} className="inline animate-spin mr-2" /> กำลังโหลด…</td></tr>
              )}
              {!loading && err && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-rose-500">{err}</td></tr>
              )}
              {!loading && !err && !displayRows.length && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">{hasFilter ? 'ไม่พบสินทรัพย์ที่ตรงกับตัวกรอง' : 'ยังไม่มีสินทรัพย์ — กด “เพิ่มสินทรัพย์” เพื่อเริ่ม'}</td></tr>
              )}
              {!loading && !err && displayRows.map((a) => {
                const age = assetAge(a.date)
                const prof = profileImage(a)
                return (
                  <tr
                    key={a.id}
                    onClick={() => navigate(`/manager/assets/${a.id}`)}
                    className="border-b border-slate-50 last:border-0 hover:bg-brand-50/40 cursor-pointer transition"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        {prof
                          ? <img src={imageThumbSrc(prof)} alt="" className="w-9 h-9 rounded-lg object-cover ring-1 ring-slate-200 shrink-0" />
                          : <span className="w-9 h-9 rounded-lg bg-slate-100 text-slate-300 flex items-center justify-center shrink-0"><Package size={16} /></span>}
                        <div className="min-w-0">
                          <div className="font-medium text-slate-800">{a.name || <span className="text-slate-400">(ไม่มีชื่อ)</span>}</div>
                          {(a.brand || a.model) && <div className="text-[12px] text-slate-400">{[a.brand, a.model].filter(Boolean).join(' · ')}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{typeLabel(byId, a.asset_type_id)}</td>
                    <td className="px-4 py-3 text-slate-500 tabular-nums">{a.serial_num || '—'}</td>
                    <td className="px-4 py-3 text-slate-600">{branchName.get(Number(a.branch_id)) || '—'}</td>
                    <td className="px-4 py-3 text-slate-600">{age ? age.label : '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <StatusPill status={a.status} />
                        {checkDueState(a).due && (
                          <span title="ถึงกำหนดตรวจเช็ก" className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10.5px] font-medium bg-amber-50 text-amber-700 ring-1 ring-amber-200">
                            <Clock size={10} className="mr-0.5" /> ตรวจ
                          </span>
                        )}
                        {/* Only the exception is tagged — most assets depreciate, so
                            badging all of them would be noise. */}
                        {!isDepreciable(a) && (
                          <span title="ลงเป็นค่าใช้จ่ายทั้งจำนวน — ไม่คิดค่าเสื่อม" className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10.5px] font-medium bg-slate-100 text-slate-600 ring-1 ring-slate-200">
                            ไม่คิดค่าเสื่อม
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-slate-600 tabular-nums">{fmtMoney(a.price)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* footer / paging */}
      <div className="flex items-center justify-between mt-4 text-[13px] text-slate-500">
        <span>{dueOnly ? `ถึงกำหนดตรวจ ${displayRows.length} รายการ` : `ทั้งหมด ${total.toLocaleString()} รายการ`}</span>
        {!dueOnly && <Pager page={page} pageTotal={pageTotal} onChange={setPage} />}
      </div>

      {scanning && (
        <QrScanner
          onClose={() => setScanning(false)}
          onDetect={(p) => { setScanning(false); navigate(checkPath(p)) }}
        />
      )}

      {editor && (
        <AssetEditor
          mode={editor.mode}
          assetId={editor.assetId}
          prefill={editor.prefill}
          types={types}
          branches={branches}
          onClose={() => setEditor(null)}
          onSaved={refresh}
          onDeleted={refresh}
        />
      )}
    </div>
  )
}
